/**
 * OPTIMISTIC UPDATE HELPERS FOR INSTANT UI FEEDBACK
 * 
 * Provides immediate cache updates for instant post-payment UX
 * (mirrors MEO's instant post-payment flow)
 * 
 * Usage:
 * 1. Initialize in campaigns list component: initGlobalOptimisticUpdate(queryClient)
 * 2. Call from PaymentPage.js: window.updateCampaignInCache(campaignId, updater)
 * 
 * Key Features:
 * - Updates ALL campaign cache keys (full, summary, aggregate)
 * - Handles multiple data structures (arrays, objects)
 * - Works with React Query as single source of truth
 * - No localStorage dependencies
 */

import { useQueryClient } from '@tanstack/react-query';

/**
 * Query keys for campaign data (matches useCampaignData.js structure)
 */
const CAMPAIGN_CACHE_KEYS = [
  ['campaigns'],           // Legacy/default key
  ['campaigns', 'full'],   // Email Campaigns page
  ['campaigns', 'summary'], // Analytics page
  ['campaigns', 'aggregate'] // Dashboard page
];

/**
 * Update a specific campaign in all React Query caches
 * This makes UI updates feel instant (<100ms) before backend confirms
 * 
 * @param {QueryClient} queryClient - React Query client instance
 * @param {string} campaignId - Campaign ID to update
 * @param {function} updater - Function that receives current campaign and returns updated campaign
 */
export const updateCampaignInAllCaches = (queryClient, campaignId, updater) => {
  console.time('[OptimisticUpdate] Total update time');
  console.log(`⚡ [OptimisticUpdate] Updating campaign ${campaignId} in all caches`);
  
  let updatedCount = 0;
  
  CAMPAIGN_CACHE_KEYS.forEach(queryKey => {
    try {
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) {
          return oldData;
        }
        
        // Handle different data structures
        if (Array.isArray(oldData)) {
          // Direct array of campaigns
          const updated = oldData.map(campaign =>
            campaign.id === campaignId ? updater(campaign) : campaign
          );
          
          if (updated.some(c => c.id === campaignId)) {
            updatedCount++;
            console.log(`✅ Updated in key: ${JSON.stringify(queryKey)}`);
          }
          
          return updated;
        } else if (oldData.campaigns && Array.isArray(oldData.campaigns)) {
          // Object with campaigns array (e.g., { campaigns: [...], total: 10 })
          const updated = {
            ...oldData,
            campaigns: oldData.campaigns.map(campaign =>
              campaign.id === campaignId ? updater(campaign) : campaign
            ),
          };
          
          if (updated.campaigns.some(c => c.id === campaignId)) {
            updatedCount++;
            console.log(`✅ Updated in key: ${JSON.stringify(queryKey)}`);
          }
          
          return updated;
        } else if (queryKey[1] === 'aggregate') {
          // Aggregate mode returns stats object, not campaigns
          // Don't modify, just return as-is
          return oldData;
        }
        
        return oldData;
      });
    } catch (error) {
      console.warn(`⚠️ Failed to update cache for key ${JSON.stringify(queryKey)}:`, error);
    }
  });
  
  console.timeEnd('[OptimisticUpdate] Total update time');
  console.log(`⚡ [OptimisticUpdate] Campaign ${campaignId} updated in ${updatedCount} cache(s)`);
  
  return updatedCount > 0;
};

/**
 * Hook to expose optimistic update function with queryClient
 * 
 * Usage in React components:
 * const { updateCampaign } = useOptimisticCampaignUpdate();
 * updateCampaign(campaignId, (campaign) => ({ ...campaign, status: 'paid' }));
 */
export const useOptimisticCampaignUpdate = () => {
  const queryClient = useQueryClient();
  
  return {
    updateCampaign: (campaignId, updater) => {
      updateCampaignInAllCaches(queryClient, campaignId, updater);
    }
  };
};

/**
 * Initialize global helper for use outside React components
 * This allows PaymentPage to update cache even though it's not in the campaigns page tree
 * 
 * Call this once in campaigns list component (e.g., EmailCampaigns.js / CreateCampaign.js):
 * 
 * const queryClient = useQueryClient();
 * React.useEffect(() => {
 *   initGlobalOptimisticUpdate(queryClient);
 * }, [queryClient]);
 */
export const initGlobalOptimisticUpdate = (queryClient) => {
  window.updateCampaignInCache = (campaignId, updater) => {
    return updateCampaignInAllCaches(queryClient, campaignId, updater);
  };
  
  console.log('⚡ [OptimisticUpdate] Global helper initialized (simplified signature)');
};

